import React, { useEffect, ReactNode } from 'react';
import { useAppSelector } from '../hooks/redux';
import { useWebSocket } from '../hooks/useWebSocket';
import { getCurrentFleetId } from '../utils/getCurrentFleet';
// import toast from 'react-hot-toast';

interface WebSocketProviderProps {
  children: ReactNode;
  url?: string;
  autoConnect?: boolean;
}

const WS_URL =
  process.env.REACT_APP_WEBSOCKET_URL || 'http://localhost:3000';

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  url,
  autoConnect = true,
}) => {
  const resolvedUrl = url || WS_URL;
  const { connect, connection, subscribeToChannel } = useWebSocket(resolvedUrl);
  const shouldAutoReconnect = useAppSelector(state => state.websocket?.shouldAutoReconnect ?? true);

  useEffect(() => {
    // Only auto-connect when the user is authenticated: the websocket
    // server rejects unauthenticated sockets, so connecting before login
    // just produces "no token provided" errors on the server.
    const hasSession =
      !!localStorage.getItem('token') || !!sessionStorage.getItem('token');

    if (autoConnect && shouldAutoReconnect && resolvedUrl && hasSession) {
      connect(resolvedUrl).catch((err) =>
        console.error('[WebSocketProvider] Auto-connect failed:', err),
      );
    }
  }, [autoConnect, shouldAutoReconnect, connect, resolvedUrl]);

  // Show connection status via toast
  useEffect(() => {
    if (connection.isConnected) {
      console.log('✅ WebSocket connected');

      // Global subscription to the fleet's company channel so real-time
      // notifications (alert.received with type=notification) reach the
      // user on EVERY page, not only the geolocation screen.
      try {
        const fleetId = getCurrentFleetId();
        if (fleetId && fleetId > 0) {
          subscribeToChannel(`company.${fleetId}`);
        }
      } catch {
        // no fleet selected yet (login screens) — skip silently
      }
    } else if (connection.error) {
      console.log(`❌ Connection error: ${connection.error}`);
    }
  }, [connection.isConnected, connection.error, subscribeToChannel]);

  return <>{children}</>;
};